// frontend/features/crm/crmThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const getAuthHeaders = (getState) => {
  const token = getState().auth.token;

  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

const buildQueryString = (paramsObj) => {
  const params = new URLSearchParams();

  Object.entries(paramsObj).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

/**
 * Pipelines
 */
export const fetchCrmPipelines = createAsyncThunk(
  "crm/fetchCrmPipelines",
  async (onlyActive = false, { getState, rejectWithValue }) => {
    try {
      const query = buildQueryString({
        active: onlyActive ? "true" : undefined,
      });

      const response = await fetch(`${API_BASE_URL}/crm/pipelines/${query}`, {
        method: "GET",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al obtener pipelines" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

// ✅ Delegación: asignar usuarios a un pipeline (admin)
export const updateCrmPipeline = createAsyncThunk(
  "crm/updateCrmPipeline",
  async ({ pipelineId, pipelineData }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/pipelines/${pipelineId}/`, {
        method: "PATCH",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(pipelineData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al actualizar pipeline" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

/**
 * DEALS
 */
export const fetchCrmDeals = createAsyncThunk(
  "crm/fetchCrmDeals",
  async ({ pipelineId, stageId, ownerId, status } = {}, { getState, rejectWithValue }) => {
    try {
      const query = buildQueryString({
        pipeline: pipelineId,
        stage: stageId,
        owner: ownerId,
        status: status,
      });

      const response = await fetch(`${API_BASE_URL}/crm/deals/${query}`, {
        method: "GET",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al obtener deals" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const createCrmDeal = createAsyncThunk(
  "crm/createCrmDeal",
  async (dealData, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/deals/`, {
        method: "POST",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(dealData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al crear el caso" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const updateCrmDeal = createAsyncThunk(
  "crm/updateCrmDeal",
  async ({ dealId, dealData }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/deals/${dealId}/`, {
        method: "PATCH",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(dealData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al actualizar el caso" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const deleteCrmDeal = createAsyncThunk(
  "crm/deleteCrmDeal",
  async (dealId, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/deals/${dealId}/`, {
        method: "DELETE",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al eliminar el caso" });
      }

      return dealId;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

/**
 * CONTACTOS
 */
export const fetchCrmContacts = createAsyncThunk(
  "crm/fetchCrmContacts",
  async ({ contactType, searchQuery } = {}, { getState, rejectWithValue }) => {
    try {
      const query = buildQueryString({
        contact_type: contactType,
        q: searchQuery,
      });

      const response = await fetch(`${API_BASE_URL}/crm/contacts/${query}`, {
        method: "GET",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al obtener contactos" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const createCrmContact = createAsyncThunk(
  "crm/createCrmContact",
  async (contactData, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/contacts/`, {
        method: "POST",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(contactData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al crear contacto" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const updateCrmContact = createAsyncThunk(
  "crm/updateCrmContact",
  async ({ contactId, contactData }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/contacts/${contactId}/`, {
        method: "PATCH",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(contactData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al actualizar contacto" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const deleteCrmContact = createAsyncThunk(
  "crm/deleteCrmContact",
  async (contactId, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/contacts/${contactId}/`, {
        method: "DELETE",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al eliminar contacto" });
      }

      return contactId;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

/**
 * SERVICIOS
 */
export const fetchCrmServices = createAsyncThunk(
  "crm/fetchCrmServices",
  async ({ onlyActive, category } = {}, { getState, rejectWithValue }) => {
    try {
      const query = buildQueryString({
        active: onlyActive ? "true" : undefined,
        category: category,
      });

      const response = await fetch(`${API_BASE_URL}/crm/services/${query}`, {
        method: "GET",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al obtener servicios" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const createCrmService = createAsyncThunk(
  "crm/createCrmService",
  async (serviceData, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/services/`, {
        method: "POST",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(serviceData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al crear servicio" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const updateCrmService = createAsyncThunk(
  "crm/updateCrmService",
  async ({ serviceId, serviceData }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/services/${serviceId}/`, {
        method: "PATCH",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(serviceData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al actualizar servicio" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const deleteCrmService = createAsyncThunk(
  "crm/deleteCrmService",
  async (serviceId, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/services/${serviceId}/`, {
        method: "DELETE",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al eliminar servicio" });
      }

      return serviceId;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

/**
 * ACTIVIDADES
 */
export const fetchCrmActivities = createAsyncThunk(
  "crm/fetchCrmActivities",
  async ({ assignedTo, contactId, dealId, isCompleted } = {}, { getState, rejectWithValue }) => {
    try {
      const query = buildQueryString({
        assigned_to: assignedTo,
        contact: contactId,
        deal: dealId,
        completed:
          isCompleted === true ? "true" : isCompleted === false ? "false" : undefined,
      });

      const response = await fetch(`${API_BASE_URL}/crm/activities/${query}`, {
        method: "GET",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al obtener actividades" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const createCrmActivity = createAsyncThunk(
  "crm/createCrmActivity",
  async (activityData, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/activities/`, {
        method: "POST",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(activityData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al crear actividad" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const updateCrmActivity = createAsyncThunk(
  "crm/updateCrmActivity",
  async ({ activityId, activityData }, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/activities/${activityId}/`, {
        method: "PATCH",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(activityData),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al actualizar actividad" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const deleteCrmActivity = createAsyncThunk(
  "crm/deleteCrmActivity",
  async (activityId, { getState, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/crm/activities/${activityId}/`, {
        method: "DELETE",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al eliminar actividad" });
      }

      return activityId;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

/**
 * COMUNICACIONES
 */
export const fetchCrmCommunications = createAsyncThunk(
  "crm/fetchCrmCommunications",
  async ({ contactId, dealId, channel, direction } = {}, { getState, rejectWithValue }) => {
    try {
      const query = buildQueryString({
        contact: contactId,
        deal: dealId,
        channel: channel,
        direction: direction,
      });

      const response = await fetch(`${API_BASE_URL}/crm/communications/${query}`, {
        method: "GET",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al obtener comunicaciones" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

/**
 * WHATSAPP (mock)
 */
export const fetchCrmWhatsAppMessages = createAsyncThunk(
  "crm/fetchCrmWhatsAppMessages",
  async ({ contactId, dealId, direction, status } = {}, { getState, rejectWithValue }) => {
    try {
      const query = buildQueryString({
        contact: contactId,
        deal: dealId,
        direction: direction,
        status: status,
      });

      const response = await fetch(`${API_BASE_URL}/crm/whatsapp-messages/${query}`, {
        method: "GET",
        headers: getAuthHeaders(getState),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al obtener mensajes de WhatsApp" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);

export const sendCrmWhatsAppMessage = createAsyncThunk(
  "crm/sendCrmWhatsAppMessage",
  async ({ contactId, dealId, toPhone, content }, { getState, rejectWithValue }) => {
    try {
      const body = {
        contact: contactId ?? null,
        deal: dealId ?? null,
        to_phone: toPhone || "",
        content: content || "",
      };

      const response = await fetch(`${API_BASE_URL}/crm/whatsapp/send/`, {
        method: "POST",
        headers: getAuthHeaders(getState),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch (e) {}
        return rejectWithValue(errorData || { detail: "Error al enviar WhatsApp" });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({ detail: error.message || "Error de red" });
    }
  }
);